import React from "react";

import { CartTotalType, TaxType } from "custom-types/Cart";
import { useSelector } from "redux/hooks";
import { getSelectedForDeliveryOption } from "redux/selectors/cart";
import {
  getDeliveryFeeWithMinumSpendFormatted,
  getDispensaryTaxCalculationEnabled,
  getDispensaryTaxType as getCartDispensaryTaxType,
} from "redux/selectors/cartDispensary";
import { getCountryCode } from "redux/selectors/location";
import { getShowCustomTax } from "utils/getShowCustomTax";

import LabeledItem from "../LabeledItem";
import TaxTotal from "../TaxTotal";

type Props = {
  totals: CartTotalType;
};

const CartTotals = ({ totals }: Props) => {
  const countryCode = useSelector(getCountryCode);
  const deliveryFee = useSelector(getDeliveryFeeWithMinumSpendFormatted);
  const selectedForDelivery = useSelector(getSelectedForDeliveryOption);
  const showCustomTax = getShowCustomTax(countryCode);

  const taxType = useSelector(getCartDispensaryTaxType);
  const taxCalculationEnabled = useSelector(getDispensaryTaxCalculationEnabled);

  const showCanadianCalculatedTaxes =
    !showCustomTax &&
    totals.hasTax &&
    (taxCalculationEnabled || taxType === TaxType.CanadianTaxTable);

  return (
    <React.Fragment>
      <LabeledItem
        data-testid="subtotal"
        className="font-bold"
        aria-label={`subtotal for ${totals.totalItems} ${
          totals?.totalItems === 1 ? "item" : "items"
        } is $${totals.subtotal}`}
        label={`Subtotal (${totals.totalItems} ${
          totals?.totalItems === 1 ? "item" : "items"
        })`}
      >
        {`$${totals.subtotal}`}
      </LabeledItem>
      {!!totals.savings && (
        <LabeledItem
          aria-label={`you are saving $${totals.savings} on this order`}
          className="text-sm pt-sm text-notification"
          label="Savings"
        >
          -${totals.savings}
        </LabeledItem>
      )}

      {showCanadianCalculatedTaxes && (
        <LabeledItem className="text-sm pt-md" label="Tax">
          ${totals.tax}
        </LabeledItem>
      )}
      {!showCanadianCalculatedTaxes && (
        <TaxTotal itemize={false} cartTotals={totals} orderSummary={false} />
      )}
      {selectedForDelivery && (
        <LabeledItem
          data-testid="delivery-fee"
          className="text-sm pt-sm"
          label="Delivery fee"
          aria-label={`delivery fee is ${deliveryFee}`}
        >
          {deliveryFee}
        </LabeledItem>
      )}
      {!!totals.total && (
        <LabeledItem
          data-testid="total"
          className="pt-md font-bold"
          aria-label={`estimated total is $${totals.total}`}
          label="Estimated total"
        >
          ${totals.total}
        </LabeledItem>
      )}
    </React.Fragment>
  );
};

export default CartTotals;
